import { z } from 'zod';
import { id, paginationQuery, paginatedResult } from './common.js';

export const taxDocumentKind = z.enum(['1099_nec', '1099_misc', 'w2', 'other']);
export const taxDocumentStatus = z.enum(['draft', 'published', 'withdrawn', 'superseded']);
export const taxYear = z.coerce.number().int().min(2000).max(2100);
const note = z
  .string()
  .trim()
  .min(3)
  .max(2000)
  .refine(
    (value) => !/\b\d{3}-\d{2}-\d{4}\b/.test(value),
    'Do not include a Social Security number. Use the secure provider tax process.'
  );
export const taxDocument = z.object({
  id,
  professionalId: id,
  professionalName: z.string(),
  year: taxYear,
  kind: taxDocumentKind,
  title: z.string(),
  issuer: z.string(),
  status: taxDocumentStatus,
  revision: z.number().int().positive(),
  hasFile: z.boolean(),
  fileBytes: z.number().int().nonnegative().nullable(),
  supersedesId: id.nullable(),
  publishedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  withdrawalReason: z.string().nullable(),
});
export type TaxDocument = z.infer<typeof taxDocument>;
export const taxDocumentAccess = z.object({ canRequest: z.boolean() });
export const taxDocumentQuery = paginationQuery.extend({
  year: taxYear.optional(),
  professionalId: id.optional(),
  status: taxDocumentStatus.optional(),
  search: z.string().trim().max(100).optional(),
});
export type TaxDocumentQuery = z.infer<typeof taxDocumentQuery>;
export const taxDocumentPage = paginatedResult(taxDocument);
export const createTaxDocumentInput = z
  .object({
    clientRequestId: z.string().uuid(),
    professionalId: id,
    year: taxYear,
    kind: taxDocumentKind,
    title: z.string().trim().min(3).max(150),
    issuer: z.string().trim().min(2).max(150),
    supersedesId: id.optional(),
  })
  .strict();
export type CreateTaxDocumentInput = z.infer<typeof createTaxDocumentInput>;
export const editTaxDocumentInput = z
  .object({
    clientRequestId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    title: z.string().trim().min(3).max(150),
    issuer: z.string().trim().min(2).max(150),
  })
  .strict();
export const uploadTaxDocumentInput = z
  .object({
    clientRequestId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    pdfBase64: z
      .string()
      .min(8)
      .max(4 * 1024 * 1024 + 100),
  })
  .strict();
export const taxDocumentActionInput = z
  .object({
    clientRequestId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    action: z.enum(['publish', 'withdraw']),
    reason: note,
    issuerDocumentConfirmed: z.boolean().optional(),
  })
  .strict();
export type TaxDocumentActionInput = z.infer<typeof taxDocumentActionInput>;
export const taxDocumentEvent = z.object({
  id,
  kind: z.string(),
  actorName: z.string(),
  body: z.string(),
  recordedAt: z.string(),
  revision: z.number().int().positive(),
});
export const taxDocumentDetail = z.object({
  document: taxDocument,
  history: paginatedResult(taxDocumentEvent),
});
export const taxRecipient = z.object({ id, name: z.string() });
export const taxRecipientPage = paginatedResult(taxRecipient);
export const taxRequestStatus = z.enum(['open', 'in_review', 'resolved', 'closed']);
export const taxRequest = z.object({
  id,
  professionalId: id,
  professionalName: z.string(),
  documentId: id.nullable(),
  year: taxYear,
  subject: z.string(),
  status: taxRequestStatus,
  revision: z.number().int().positive(),
  createdAt: z.string(),
  updatedAt: z.string(),
  availableActions: z.array(z.enum(['comment', 'start_review', 'resolve', 'close', 'reopen'])),
});
export type TaxRequest = z.infer<typeof taxRequest>;
export const taxRequestPage = paginatedResult(taxRequest);
export const taxRequestQuery = paginationQuery.extend({
  year: taxYear.optional(),
  status: taxRequestStatus.optional(),
  search: z.string().trim().max(100).optional(),
});
export type TaxRequestQuery = z.infer<typeof taxRequestQuery>;
export const createTaxRequestInput = z
  .object({
    clientRequestId: z.string().uuid(),
    year: taxYear,
    documentId: id.optional(),
    subject: z.string().trim().min(3).max(150),
    body: note,
  })
  .strict();
export type CreateTaxRequestInput = z.infer<typeof createTaxRequestInput>;
export const taxRequestActionInput = z
  .object({
    clientRequestId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    action: z.enum(['comment', 'start_review', 'resolve', 'close', 'reopen']),
    body: note,
  })
  .strict();
export type TaxRequestActionInput = z.infer<typeof taxRequestActionInput>;
export const taxRequestEvent = z.object({
  id,
  kind: z.string(),
  actorName: z.string(),
  body: z.string(),
  recordedAt: z.string(),
  revision: z.number().int().positive(),
});
export const taxRequestDetail = z.object({
  request: taxRequest,
  history: paginatedResult(taxRequestEvent),
});
